import { PERMISSION, normalizePermissionLevels } from "./permissions";

const STORAGE_KEYS = {
  TOKEN: "token",
  REFRESH: "refresh",
  PROFILE: "profile",
  PERMISSIONS: "permission",
  AUTH_CHECK: "authcheck",
};

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const readAuthFromStorage = () => {
  const profile = safeParse(localStorage.getItem(STORAGE_KEYS.PROFILE)) || null;
  const permissionsRaw =
    safeParse(localStorage.getItem(STORAGE_KEYS.PERMISSIONS)) || [];
  const permissions = Array.isArray(permissionsRaw)
    ? permissionsRaw.map((p) => ({
        ...p,
        menu_id: Number(p.menu_id),
        permission_level: normalizePermissionLevels(p.permission_level),
      }))
    : [];

  return {
    token: localStorage.getItem(STORAGE_KEYS.TOKEN) || null,
    refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH) || null,
    profile,
    permissions,
  };
};

export const persistAuth = ({ token, refreshToken, profile, permissions }) => {
  if (token) localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  if (refreshToken) localStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken);
  if (profile)
    localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(profile));
  if (permissions)
    localStorage.setItem(
      STORAGE_KEYS.PERMISSIONS,
      JSON.stringify(permissions),
    );
};

export const persistTokens = ({ token, refreshToken }) => {
  if (token) localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  if (refreshToken) localStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken);
};

export const getStoredRefreshToken = () =>
  localStorage.getItem(STORAGE_KEYS.REFRESH) || null;

export const clearStoredAuth = () => {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
};

// Capture the raw (already-serialized) auth keys so a login attempt can be
// rolled back. Lets the login flow be atomic: a failed sign-in must not log
// the user out of an existing session. Includes AUTH_CHECK because it is sent
// as the `auth-token` header on every authenticated request, not just login.
export const snapshotAuth = () =>
  Object.fromEntries(
    Object.values(STORAGE_KEYS).map((key) => [key, localStorage.getItem(key)]),
  );

export const restoreAuth = (snapshot) => {
  if (!snapshot) return;
  Object.entries(snapshot).forEach(([key, value]) => {
    if (value != null) localStorage.setItem(key, value);
  });
};

// Pure checks for non-React contexts (axios interceptor, thunks).
// React components should use `usePermissions()` instead so re-renders
// track redux updates.
export const isSuperAdminFromStorage = () => {
  const { profile } = readAuthFromStorage();
  return profile?.is_super_admin === true;
};

export const hasActionFromStorage = (menuId, action = PERMISSION.VIEW) => {
  const { profile, permissions } = readAuthFromStorage();
  if (profile?.is_super_admin) return true;
  const entry = permissions.find((p) => p.menu_id === Number(menuId));
  return entry ? entry.permission_level.includes(action) : false;
};
